import { format, isPast, isToday } from 'date-fns';
import { useLocation } from 'wouter';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { CalendarClock, Pencil, User } from 'lucide-react';
import { formatClaimReference, isTaskClosed, type TaskClaimSummary } from '@/lib/task-filters';
import type { ClaimTask, TaskPriority } from '@/api/generated/client';
import { Button } from '@/components/ui/button';

interface TaskListItemDetailedProps {
  task: ClaimTask;
  claim?: TaskClaimSummary;
  onTaskComplete?: (taskId: number, claimId: number) => void;
  onTaskEdit?: (task: ClaimTask) => void;
}

const priorityConfig: Record<TaskPriority, { label: string; badgeClassName: string; accentClassName: string }> = {
  urgent: { label: 'Urgent', badgeClassName: 'bg-red-500 hover:bg-red-600', accentClassName: 'bg-red-500' },
  high: { label: 'High', badgeClassName: 'bg-orange-500 hover:bg-orange-600', accentClassName: 'bg-orange-500' },
  medium: { label: 'Medium', badgeClassName: 'bg-yellow-500 hover:bg-yellow-600', accentClassName: 'bg-yellow-500' },
  low: { label: 'Low', badgeClassName: 'bg-blue-500 hover:bg-blue-600', accentClassName: 'bg-blue-500' }
};

/**
 * Detailed task row used by the flat "list" view (TaskListView). Shows a priority
 * accent bar, an inline due-date indicator, and the priority badge stacked under
 * the due date. The calendar view's day-scoped panel uses the simpler
 * `TaskListItem` component instead.
 */
export function TaskListItemDetailed({ task, claim, onTaskComplete, onTaskEdit }: TaskListItemDetailedProps) {
  const [, setLocation] = useLocation();
  const canWrite = claim?.canWrite ?? false;
  const showEditAction = canWrite && !!onTaskEdit;
  const isCompleted = task.status === 'completed';
  // A cancelled task is "closed" like a completed one — it shouldn't read as
  // overdue/active, and its checkbox shouldn't be actionable. `isCompleted`
  // above is kept separate and only drives the checkbox's checked state,
  // since a cancelled task was never actually completed.
  const isClosed = isTaskClosed(task);
  const priority = priorityConfig[task.priority];

  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  const isOverdue = !!dueDate && isPast(dueDate) && !isToday(dueDate) && !isClosed;
  const isDueToday = !!dueDate && isToday(dueDate);

  const dueDateLabel = dueDate
    ? isOverdue
      ? `Overdue \u00b7 ${format(dueDate, 'MMM d')}`
      : isDueToday
        ? 'Due today'
        : `Due ${format(dueDate, 'MMM d')}`
    : null;

  const handleCheckboxChange = (checked: CheckedState) => {
    if (checked === true && !isClosed && onTaskComplete) {
      onTaskComplete(task.id, task.claimId);
    }
  };

  return (
    <div
      className={cn(
        'group relative flex gap-3 overflow-hidden rounded-lg border bg-card p-3 pl-4 shadow-sm transition-all',
        'hover:shadow-md hover:border-primary/50',
        isOverdue && 'border-red-500/50 bg-red-500/5'
      )}
    >
      {/* Priority accent bar */}
      <span className={cn('absolute inset-y-0 left-0 w-1', priority.accentClassName)} aria-hidden="true" />

      {/* Checkbox for completion */}
      <div className="pt-0.5">
        <Checkbox
          checked={isCompleted}
          onCheckedChange={handleCheckboxChange}
          disabled={isClosed || !canWrite}
          className="h-5 w-5"
        />
      </div>

      {/* Task content - clickable */}
      <div className="relative min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setLocation(`/claims/${task.claimId}`)}
          className="w-full min-w-0 text-left focus:outline-none"
        >
          <div className="space-y-1.5">
            {/* Title and due date */}
            <div className={cn(
              'flex flex-col items-start gap-1 sm:flex-row sm:justify-between sm:gap-2',
              showEditAction && 'pr-8'
            )}>
              <h3 className={cn(
                'font-medium leading-snug line-clamp-3',
                isClosed && 'text-muted-foreground'
              )}>
                {task.title}
              </h3>

              {dueDateLabel && (
                <span
                  data-testid="task-due-date"
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs',
                    isOverdue ? 'font-medium text-red-600' : isDueToday ? 'font-medium text-primary' : 'text-muted-foreground'
                  )}
                >
                  <CalendarClock className="h-3.5 w-3.5" />
                  {dueDateLabel}
                </span>
              )}
            </div>

            {/* Priority + assignee + claim reference */}
            <div className={cn(
              'flex flex-col items-start gap-1.5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:gap-2',
              showEditAction && 'pr-8'
            )}>
              <div className="flex min-w-0 items-center gap-2">
                {claim && (
                  <>
                    <span className="truncate">{formatClaimReference(claim)}</span>
                    {!canWrite && (
                      <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0 text-amber-700 border-amber-300 bg-amber-50">
                        View only
                      </Badge>
                    )}
                  </>
                )}
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {task.assignedToName && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1 gap-1 shrink-0 text-blue-600 border-blue-200 bg-blue-50">
                    <User className="h-3 w-3" />
                    {task.assignedToName}
                  </Badge>
                )}
                <Badge className={cn('shrink-0 text-[10px] h-4 px-1', priority.badgeClassName)}>
                  {priority.label}
                </Badge>
              </div>
            </div>
          </div>
        </button>
        {showEditAction && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Edit ${task.title}`}
            title="Edit task"
            data-testid={`task-edit-${task.id}`}
            className="absolute bottom-0 right-0 h-7 w-7 shrink-0 p-0"
            onClick={() => onTaskEdit(task)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
